import axios from 'axios';

/**
 * Cliente API para obtener temas desde el backend de ACV
 * 
 * IMPORTANTE: Este cliente debe funcionar tanto en desarrollo (localhost)
 * como en producción (ngrok, dominio real), detectando automáticamente
 * la URL del backend y el tenant slug.
 */
class ThemesApi {
  /**
   * Obtiene la URL base del API de ACV
   * Prioridad:
   * 1. Variable de entorno VITE_ACV_BACKEND_API
   * 2. Variable window inyectada (ACV_API_URL)
   * 3. Inferencia desde hostname (ngrok -> api.acv.app, localhost -> localhost:8000)
   * 4. Fallback a localhost para desarrollo
   */
  private getApiBaseUrl(): string {
    // 1. Intentar desde variable de entorno Vite
    if (import.meta.env.VITE_ACV_BACKEND_API) {
      const apiUrl = import.meta.env.VITE_ACV_BACKEND_API.trim();
      if (apiUrl) {
        // console.log('[ThemesApi] Using API URL from VITE_ACV_BACKEND_API:', apiUrl);
        return apiUrl;
      }
    }

    // 2. Intentar desde variable window inyectada
    if (typeof window !== 'undefined' && (window as any).ACV_API_URL) {
      const apiUrl = (window as any).ACV_API_URL;
      // console.log('[ThemesApi] Using API URL from window.ACV_API_URL:', apiUrl);
      return apiUrl;
    }

    // 3. Inferir desde hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('ngrok') || hostname.includes('ngrok-free.app')) {
        // console.log('[ThemesApi] Using production API URL (ngrok detected)');
        return 'https://api.acv.app';
      }
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // console.log('[ThemesApi] Using localhost API URL');
        return 'http://localhost:8000';
      }
    }

    // 4. Fallback a localhost para desarrollo
    // console.warn('[ThemesApi] ⚠️ No API URL configured, using localhost fallback');
    return 'http://localhost:8000';
  }

  /**
   * Obtiene el tenant slug
   * Prioridad:
   * 1. URL parameter (?slug=...)
   * 2. localStorage.user.slug
   * 3. localStorage.msg.user.slug
   * 4. localStorage.slug
   */
  private getTenantSlug(): string | null {
    if (typeof window === 'undefined') return null;

    // 1. Desde URL
    const urlParams = new URLSearchParams(window.location.search);
    let slug = urlParams.get('slug');
    
    // Limpiar slug si viene con prefijo ?slug=
    if (slug && typeof slug === 'string' && slug.startsWith('?slug=')) {
      slug = slug.replace('?slug=', '');
    }
    
    if (slug) {
      return slug;
    }

    // 2. Desde localStorage.slug
    const storedSlug = localStorage.getItem('slug');
    if (storedSlug) {
      return storedSlug;
    }

    // 3. Desde localStorage.user
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.slug) {
          slug = user.slug;
          if (typeof slug === 'string' && slug.startsWith('?slug=')) {
            slug = slug.replace('?slug=', '');
          }
          if (slug) {
            return slug;
          }
        }
      }
    } catch (e) {
      // console.warn('[ThemesApi] Error parsing localStorage.user:', e);
    }

    // 4. Desde localStorage.msg.user
    try {
      const msgStr = localStorage.getItem('msg');
      if (msgStr) {
        const msg = JSON.parse(msgStr);
        if (msg?.user?.slug) {
          slug = msg.user.slug;
          if (typeof slug === 'string' && slug.startsWith('?slug=')) {
            slug = slug.replace('?slug=', '');
          }
          if (slug) {
            return slug;
          }
        }
      }
    } catch (e) {
      // console.warn('[ThemesApi] Error parsing localStorage.msg:', e);
    }

    return null;
  }

  /**
   * Obtiene el access token para autenticación
   */
  private getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('access_token') || 
           localStorage.getItem('token') ||
           null;
  }

  /**
   * Obtiene el email del usuario
   * Prioridad:
   * 1. URL parameter (?email=...)
   * 2. localStorage.userEmail
   * 3. localStorage.user.mail/email
   * 4. localStorage.msg.user.mail/email
   */
  private getUserEmail(): string | null {
    if (typeof window === 'undefined') return null;

    // 1. Intentar desde URL
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get('email') || urlParams.get('userEmail');
    if (emailFromUrl) {
      // console.log('[ThemesApi] Email obtenido desde URL:', emailFromUrl);
      return emailFromUrl;
    }

    // 2. Intentar desde localStorage.userEmail
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      // console.log('[ThemesApi] Email obtenido desde localStorage.userEmail:', userEmail);
      return userEmail;
    }

    // 3. Intentar desde localStorage.user
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.mail) {
          // console.log('[ThemesApi] Email obtenido desde localStorage.user.mail:', user.mail);
          return user.mail;
        }
        if (user?.email) {
          // console.log('[ThemesApi] Email obtenido desde localStorage.user.email:', user.email);
          return user.email;
        }
      }
    } catch (e) {
      // console.warn('[ThemesApi] Error parsing localStorage.user:', e);
    }

    // 4. Intentar desde postMessage
    try {
      const msgStr = localStorage.getItem('msg');
      if (msgStr) {
        const msg = JSON.parse(msgStr);
        if (msg?.user?.mail) {
          // console.log('[ThemesApi] Email obtenido desde localStorage.msg.user.mail:', msg.user.mail);
          return msg.user.mail;
        }
        if (msg?.user?.email) {
          // console.log('[ThemesApi] Email obtenido desde localStorage.msg.user.email:', msg.user.email);
          return msg.user.email;
        }
      }
    } catch (e) {
      // console.warn('[ThemesApi] Error parsing localStorage.msg:', e);
    }

    // console.warn('[ThemesApi] ⚠️ No se pudo obtener email de usuario desde ninguna fuente');
    return null;
  }

  /**
   * Maneja la respuesta de la API y extrae el tema
   */
  private handleApiResponse(response: any): any {
    if (typeof response?.data === 'string') {
      try {
        let parsedData = JSON.parse(response.data);
        if (typeof parsedData === 'string') {
          parsedData = JSON.parse(parsedData);
        }
        response = { ...response, data: parsedData };
      } catch (e) {
        // console.error('[ThemesApi] Error parseando response.data:', e);
      }
    }

    if (response?.data?.data?.theme) {
      // console.log('[ThemesApi] ✅ Tema encontrado en response.data.data.theme');
      return response.data.data.theme;
    }

    if (response?.data?.theme) {
      // console.log('[ThemesApi] ✅ Tema encontrado en response.data.theme');
      return response.data.theme;
    }

    if (response?.data?.result) {
      // console.log('[ThemesApi] ✅ Tema encontrado en response.data.result');
      return response.data.result;
    }

    if (response?.result) {
      // console.log('[ThemesApi] ✅ Tema encontrado en response.result');
      return response.result;
    }

    if (response?.data) {
      // console.warn('[ThemesApi] ⚠️ Usando response.data completo como último recurso');
      return response.data;
    }

    // console.warn('[ThemesApi] ⚠️ No se pudo extraer el tema de la respuesta');
    return response;
  }

  /**
   * Obtiene el tema del módulo desde el backend
   */
  async getModuleTheme(moduleId: string): Promise<any> {
    const apiBaseUrl = this.getApiBaseUrl();
    const slug = this.getTenantSlug();
    const accessToken = this.getAccessToken();
    const userEmail = this.getUserEmail();

    if (!slug) {
      // console.warn('[ThemesApi] No tenant slug found, cannot fetch theme');
      return null;
    }

    let url = `${apiBaseUrl}/api/themes/user/module/${moduleId}?slug=${slug}`;
    
    if (userEmail) {
      url += `&email=${encodeURIComponent(userEmail)}&userEmail=${encodeURIComponent(userEmail)}`;
    }

    // console.log('[ThemesApi] Fetching theme:', {
//       url,
//       moduleId,
//       slug,
//       hasToken: !!accessToken,
//       hasEmail: !!userEmail
//     });

    try {
      const response = await axios.get(url, {
        headers: accessToken ? {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        } : {
          'Content-Type': 'application/json'
        }
      });

      // ⚠️ CRÍTICO: Si response.data es string JSON, parsearlo ANTES de procesarlo
      let parsedResponse = response;
      if (typeof response.data === 'string') {
        // console.log('[ThemesApi] ⚠️ response.data es STRING, parseando JSON...');
        try {
          parsedResponse = {
            ...response,
            data: JSON.parse(response.data)
          };
          // console.log('[ThemesApi] ✅ JSON parseado correctamente');
        } catch (e) {
          // console.error('[ThemesApi] ❌ Error parseando JSON:', e);
          return null;
        }
      }

      const theme = this.handleApiResponse(parsedResponse);
      
      // console.log('[ThemesApi] Theme fetched successfully:', {
//         themeId: theme?.id,
//         themeName: theme?.name,
//         family: theme?.family,
//         hasColors: !!theme?.colors
//       });

      if (!theme || !theme.id) {
        // console.error('[ThemesApi] ❌ Theme response is invalid or empty:', theme);
        return null;
      }

      return theme;
    } catch (error: any) {
      // console.error('[ThemesApi] Error fetching theme:', {
//         error: error.message,
//         status: error.response?.status,
//         url
//       });
      return null;
    }
  }

  /**
   * Obtiene un tema por ID
   */
  async getThemeById(themeId: string): Promise<any> {
    const apiBaseUrl = this.getApiBaseUrl();
    const slug = this.getTenantSlug();
    const accessToken = this.getAccessToken();
    const userEmail = this.getUserEmail();

    if (!slug) {
      // console.warn('[ThemesApi] No tenant slug found, cannot fetch theme by ID');
      return null;
    }

    let url = `${apiBaseUrl}/api/themes/${themeId}?slug=${slug}`;
    if (userEmail) {
      url += `&email=${encodeURIComponent(userEmail)}`;
      url += `&userEmail=${encodeURIComponent(userEmail)}`;
    }

    // console.log('[ThemesApi] Fetching theme by ID:', {
//       url,
//       themeId,
//       slug,
//       userEmail: userEmail || 'null',
//       hasToken: !!accessToken
//     });

    try {
      const response = await axios.get(url, {
        headers: accessToken ? {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        } : {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        validateStatus: (status) => status < 500
      });

      if (response.status >= 400) {
        // console.warn('[ThemesApi] API returned error status:', {
//           status: response.status,
//           statusText: response.statusText,
//           url,
//           themeId
//         });
        return null;
      }

      // ⚠️ CRÍTICO: Si response.data es string JSON, parsearlo ANTES de procesarlo
      let parsedResponse = response;
      if (typeof response.data === 'string') {
        // console.log('[ThemesApi] ⚠️ response.data es STRING, parseando JSON...');
        try {
          parsedResponse = {
            ...response,
            data: JSON.parse(response.data)
          };
          // console.log('[ThemesApi] ✅ JSON parseado correctamente');
        } catch (e) {
          // console.error('[ThemesApi] ❌ Error parseando JSON:', e);
          return null;
        }
      }

      const theme = this.handleApiResponse(parsedResponse);
      
      // console.log('[ThemesApi] Theme by ID fetched successfully:', {
//         themeId: theme?.id,
//         themeName: theme?.name,
//         family: theme?.family,
//         hasColors: !!theme?.colors
//       });

      return theme;
    } catch (error: any) {
      // console.error('[ThemesApi] Error fetching theme by ID:', {
//         error: error.message,
//         status: error.response?.status,
//         statusText: error.response?.statusText,
//         themeId,
//         url
//       });
      return null;
    }
  }
}

export const ThemesApiClient = new ThemesApi();

